import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { supabase } from '@/lib/supabase'
import { db, nowISO, type LocalUser } from '@/lib/db'
import type { Phase, Week, VolumeTier, UnitSystem } from '@/types/common'

interface UserProfile {
  id: string
  email: string
  display_name?: string
  unit_system: UnitSystem
  current_phase: Phase
  current_week: Week
  volume_tier: VolumeTier
  trt_dose_mg?: number
}

interface UserState {
  profile: UserProfile | null
  loading: boolean
  error: string | null

  // Actions
  loadProfile: (userId: string) => Promise<void>
  updateProfile: (updates: Partial<UserProfile>) => Promise<void>
  createProfile: (userId: string, email: string) => Promise<void>
  advanceWeek: () => Promise<void>
  setPhase: (phase: Phase) => Promise<void>
  setWeek: (week: Week) => Promise<void>
  setVolumeTier: (tier: VolumeTier) => Promise<void>
  setUnitSystem: (system: UnitSystem) => Promise<void>
}

// Helper to convert UserProfile to LocalUser for IndexedDB
function toLocalUser(profile: UserProfile): LocalUser {
  return {
    ...profile,
    updated_at: nowISO(),
  }
}

// Helper to convert LocalUser to UserProfile
function toUserProfile(local: LocalUser): UserProfile {
  return {
    id: local.id,
    email: local.email,
    display_name: local.display_name,
    unit_system: local.unit_system,
    current_phase: local.current_phase as Phase,
    current_week: local.current_week as Week,
    volume_tier: local.volume_tier as VolumeTier,
    trt_dose_mg: local.trt_dose_mg,
  }
}

export const useUserStore = create<UserState>()(
  persist(
    (set, get) => ({
      profile: null,
      loading: false,
      error: null,

      loadProfile: async (userId: string) => {
        set({ loading: true, error: null })

        try {
          // Try to load from Supabase
          const { data, error } = await supabase
            .from('users')
            .select('*')
            .eq('id', userId)
            .single()

          if (error && error.code !== 'PGRST116') {
            // PGRST116 = no rows found
            throw error
          }

          if (data) {
            const profile: UserProfile = {
              id: data.id,
              email: data.email,
              display_name: data.display_name ?? undefined,
              unit_system: data.unit_system as UnitSystem,
              current_phase: data.current_phase as Phase,
              current_week: data.current_week as Week,
              volume_tier: data.volume_tier as VolumeTier,
              trt_dose_mg: data.trt_dose_mg ?? undefined,
            }

            // Cache in IndexedDB
            await db.users.put(toLocalUser(profile))

            set({ profile, loading: false })
          } else {
            // No profile found - will need to create one
            set({ loading: false })
          }
        } catch (error) {
          console.error('Load profile error:', error)

          // Try to load from local cache
          const cached = await db.users.get(userId)
          if (cached) {
            set({ profile: toUserProfile(cached), loading: false })
          } else {
            set({
              loading: false,
              error: error instanceof Error ? error.message : 'Failed to load profile',
            })
          }
        }
      },

      createProfile: async (userId: string, email: string) => {
        set({ loading: true, error: null })

        const newProfile: UserProfile = {
          id: userId,
          email,
          unit_system: 'imperial',
          current_phase: 1,
          current_week: 1,
          volume_tier: 2,
        }

        try {
          const { error } = await supabase.from('users').insert({
            id: userId,
            email,
            unit_system: 'imperial' as const,
            current_phase: 1,
            current_week: 1,
            volume_tier: 2,
          })

          if (error) throw error

          // Cache locally
          await db.users.put(toLocalUser(newProfile))

          set({ profile: newProfile, loading: false })
        } catch (error) {
          console.error('Create profile error:', error)

          // Store locally for later sync
          await db.users.put(toLocalUser(newProfile))

          set({
            profile: newProfile,
            loading: false,
            error: error instanceof Error ? error.message : 'Profile saved locally',
          })
        }
      },

      updateProfile: async (updates: Partial<UserProfile>) => {
        const { profile } = get()
        if (!profile) return

        const updatedProfile = { ...profile, ...updates }

        // Optimistic update
        set({ profile: updatedProfile })

        try {
          const { error } = await supabase
            .from('users')
            .update({
              display_name: updates.display_name,
              unit_system: updates.unit_system,
              current_phase: updates.current_phase,
              current_week: updates.current_week,
              volume_tier: updates.volume_tier,
              trt_dose_mg: updates.trt_dose_mg,
            })
            .eq('id', profile.id)

          if (error) throw error

          // Update local cache
          await db.users.put(toLocalUser(updatedProfile))
        } catch (error) {
          console.error('Update profile error:', error)
          // Keep the optimistic update - will sync later
          await db.users.put(toLocalUser(updatedProfile))
        }
      },

      advanceWeek: async () => {
        const { profile, updateProfile } = get()
        if (!profile) return

        let newWeek = profile.current_week + 1
        let newPhase = profile.current_phase

        if (newWeek > 8) {
          newWeek = 1
          newPhase = profile.current_phase === 1 ? 2 : 1
        }

        await updateProfile({
          current_week: newWeek as Week,
          current_phase: newPhase as Phase,
        })
      },

      setPhase: async (phase: Phase) => {
        const { updateProfile } = get()
        await updateProfile({ current_phase: phase, current_week: 1 })
      },

      setWeek: async (week: Week) => {
        const { updateProfile } = get()
        await updateProfile({ current_week: week })
      },

      setVolumeTier: async (tier: VolumeTier) => {
        const { updateProfile } = get()
        await updateProfile({ volume_tier: tier })
      },

      setUnitSystem: async (system: UnitSystem) => {
        const { updateProfile } = get()
        await updateProfile({ unit_system: system })
      },
    }),
    {
      name: 'xframe-user',
      partialize: (state) => ({
        profile: state.profile,
      }),
    }
  )
)
